<template>
    <div>
        <div class="personal-info__separator"></div>
        <div class="modal-content">
            <div class="personal-info__sub-heading">{{ $t('contactUs:clientInformation.appointment') }}</div>
            <ValidationObserver v-slot="{ passes }" slim>
                <form novalidate name="client-details-form form" @submit.prevent="passes(onSubmitForm)" >
                    <ValidationProvider
                        v-if="result < 5000"
                        tag="div"
                        class="product-choice"
                        :rules="{ required: { allowFalse: (productChoice.UBSPayWorldwide || productChoice.UBSPaymentTracking) } }"
                        v-slot="{ errors }">
                          <div class="invalid-feedback" v-if="errors[0]">{{ $t('error:product_choice') }}</div>
                          <Checkbox v-model="productChoice.UBSPayWorldwide"
                            :label="$t('contactUs:productChoice.UBSPayWorldwide')"
                            :true-value="true"
                            :false-value="false"
                            required/>
                          <Checkbox v-model="productChoice.UBSPaymentTracking"
                            :label="$t('contactUs:productChoice.UBSPaymentTracking')"
                            :true-value="true"
                            :false-value="false"
                            required/>
                    </ValidationProvider>
                    <RadioSelect class="custom-style" v-model="primaryInformation.title" :options="salutationOptions" required inline/>
                    <TextField v-model="primaryInformation.firstName"
                      :label="$t('contactUs:firstName.label')"
                      required/>
                    <TextField v-model="primaryInformation.lastName"
                      :label="$t('contactUs:lastName.label')"
                      required/>
                    <TextField v-model="primaryInformation.company"
                      :label="$t('contactUs:company.label')"
                      required/>
                    <ValidationProvider tag="div"
                        :rules="{ required: { allowFalse: (contactPreference.companyPhone || contactPreference.companyEmail) } }"
                        v-slot="{ errors }">
                        <div class="invalid-feedback" v-if="errors[0]">{{ $t('error:checkbox') }}</div>
                        <Checkbox v-model="contactPreference.companyPhone"
                          :label="$t('contactUs:phone.label')"
                          :true-value="true"
                          :false-value="false"/>
                        <Checkbox v-model="contactPreference.companyEmail"
                          :label="$t('contactUs:email.label')"
                          :true-value="true"
                          :false-value="false"/>
                    </ValidationProvider>
                    <TextField v-if="contactPreference.companyPhone" v-model="primaryInformation.phoneNumber"
                      input-type="tel"
                      :label="$t('contactUs:phone.placeholder')"
                      rules="phone"
                      required/>
                    <TextField v-if="contactPreference.companyEmail" v-model="primaryInformation.email"
                      input-type="email"
                      :label="$t('contactUs:email.label')"
                      required/>
                    <SelectCustom v-model="primaryInformation.preferredCallbackHour"
                        :options="timeTocallOptions"
                        required/>

                    <TextField v-model="primaryInformation.message"
                      :label="$t('contactUs:news.label')"
                      required/>

                    <div v-if="consent.links && consent.links.length > 0" class="term-links">
                        <a v-for="(link, index) in consent.links"
                            :key="index"
                            class="link"
                            :href="link.url"
                            target="_blank">
                            <SvgIcon type="icon_arrow_right_blue" class="icon" alt="Tooltip"/>
                            {{ link.anchor }}
                        </a>
                    </div>
                    <div v-if="consent.id" class="form-group required">
                        <label class="control-label" for="lastName">{{ consent.confirmLabel }}</label>
                        <ValidationProvider v-slot="{ errors }"
                                            vid="consent"
                                            tag="div"
                                            class="custom-checkbox"
                                            :rules="{ required: { allowFalse: false } }">
                            <div class="errors" v-if="errors[0]">
                                {{ $t('error:required') }}
                            </div>
                            <input id="consent"
                                    type="checkbox"
                                    class="custom-style"
                                    v-model="consentCheck"
                                    required>
                            <label for="consent" v-html="consent.confirmText" />
                        </ValidationProvider>
                    </div>

                    <div class="form-group">
                        <div class="btn-panel">
                            <button type="submit"
                                    class="btn btn-carbon"
                                    @click="passes(onSubmitForm)"
                                    :disabled="disabled">{{ $t('contactUs:submit.label') }}
                            </button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { required, email, regex } from 'vee-validate/dist/rules'
import { phone } from '@/validation-rules'
import { mapState, mapGetters, mapActions } from 'vuex'
import { HTTP } from '@/http-common'
import TextField from '@/components/form/TextField'
import RadioSelect from '@/components/form/RadioSelect'
import SelectCustom from '@/components/form/SelectCustom'
import Checkbox from '@/components/form/Checkbox'
import SvgIcon from '@/components/SvgIcon'
import adobeTracking from '@/mixins/adobeTracking'

extend('required', required)
extend('regex', regex)
extend('email', email)
extend('phone', phone)

export default {
    name: 'ContactUs',
    data: () => ({
        primaryInformation: {
            firstName: '',
            lastName: '',
            email: '',
            title: '',
            company: '',
            phoneNumber: '',
            message: '',
            preferredCallbackHour: ''
        },
        contactPreference: {
            companyPhone: true,
            companyEmail: false
        },
        productChoice: {
            UBSPayWorldwide: false,
            UBSPaymentTracking: false
        },
        modalName: 'contact-us',
        consentCheck: false,
        formStepName: 'contactUs',
        disabled: false
    }),
    mixins: [
        adobeTracking
    ],
    components: {
        ValidationObserver,
        ValidationProvider,
        TextField,
        RadioSelect,
        Checkbox,
        SelectCustom,
        SvgIcon
    },
    computed: {
        ...mapState('app', {
            language: 'language',
            consent: 'consent',
            appMetadata: 'metadata'
        }),
        ...mapGetters('question', [
            'currentQuestion',
            'allQuestions',
            'values',
            'result'
        ]),
        salutationOptions() {
            return [
                { value: 'mrs', label: this.$i18n.t('contactUs:title.ms') },
                { value: 'mr', label: this.$i18n.t('contactUs:title.mr') }
            ]
        },
        timeTocallOptions() {
            return [
                { value: this.$i18n.t('contactUs:timeToCall.time.slot1'), label: this.$i18n.t('contactUs:timeToCall.time.slot1') },
                { value: this.$i18n.t('contactUs:timeToCall.time.slot2'), label: this.$i18n.t('contactUs:timeToCall.time.slot2') },
                { value: this.$i18n.t('contactUs:timeToCall.time.slot3'), label: this.$i18n.t('contactUs:timeToCall.time.slot3') },
                { value: this.$i18n.t('contactUs:timeToCall.time.slot4'), label: this.$i18n.t('contactUs:timeToCall.time.slot4') },
                { value: this.$i18n.t('contactUs:timeToCall.time.slot5'), label: this.$i18n.t('contactUs:timeToCall.time.slot5') }
            ]
        }
    },
    mounted() {
        this.trackFormStep(this.formStepName)
        this.loadConsent({ formType: (this.result < 5000) ? 'formB' : 'formA' })
    },
    methods: {
        ...mapActions('app', [
            'setLoading',
            'loadConsent'
        ]),

        async onSubmitForm() {
            this.disabled = true
            this.setLoading(true)
            const payload = {
                fields: {
                    ...this.primaryInformation,
                    gender: this.primaryInformation.title === 'mr' ? '1' : '2',
                    sector: this.values.companyIndustry,
                    region: this.values.activeRegions,
                    percentage: this.values.annualShare,
                    howHighSales: this.values.annualTurnover,
                    contactPreference: Object.keys(this.contactPreference).reduce((arr, key) => {
                        (this.contactPreference[key]) && arr.push(key)
                        return arr
                    }, []),
                    productChoice: Object.keys(this.productChoice).reduce((arr, key) => {
                        (this.productChoice[key]) && arr.push(key)
                        return arr
                    }, []),
                    result: this.result,
                    consent: this.consent.id
                },
                metaData: {
                    language: this.language,
                    formPageUrl: (document.referrer) ? document.referrer : document.location.href,
                    campID: this.appMetadata.campID,
                    channel: this.appMetadata.channel,
                    aecID: this.appMetadata.aecID
                }
            }
            try {
                await HTTP.post('/submit-form', payload)
                this.trackFormSuccess()
                this.$emit('close', true)
                if (this.$route.name !== 'success') {
                    this.$router.push({
                        name: 'success'
                    })
                }
            } catch (error) {
                console.log('submitForm::catch()', error)
                this.setLoading(false)
                this.trackFormError(this.formStepName, error)
            }
        }
    }
}

</script>
